import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './styles/App.css';

import AuthScreen  from './components/AuthScreen';
import Sidebar     from './components/Sidebar';
import Chat        from './components/Chat';
import JobsView    from './components/JobsView';
import IdeasView   from './components/IdeasView';
import KnowledgeView from './components/KnowledgeView';
import PostingView from './components/PostingView';
import HelpModal   from './components/HelpModal';
import ErrorBoundary from './components/ErrorBoundary';
import { migrate, get as lsGet, set as lsSet, remove as lsRemove, Keys } from './lib/storage';
import { makeApi } from './lib/api';
import useChats    from './hooks/useChats';

// localStorage Schema-Migration läuft einmalig beim Modul-Load
migrate();

export default function App() {
  const [authToken, setAuthToken] = useState(() => lsGet(Keys.TOKEN, ''));
  const [tab, setTab]             = useState('chat');
  const [theme, setTheme]         = useState(() => lsGet(Keys.THEME, 'dark'));
  const [sidebarOpen, setSidebarOpen] = useState(false);  // Mobile-only
  const [helpSection, setHelpSection] = useState(null);   // null = Hilfe zu; sonst Abschnitts-ID
  const [pendingOpenIdea, setPendingOpenIdea] = useState(null);  // Posting → Ideen-Modal öffnen
  const [currentChatId, setCurrentChatId] = useState(() => {
    const v = lsGet(Keys.CHAT_ID, null);
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
  });

  const logout = useCallback(() => {
    lsRemove(Keys.TOKEN);
    lsRemove(Keys.CHAT_ID);
    setAuthToken('');
    setCurrentChatId(null);
  }, []);

  // Theme auf <html data-theme=...> spiegeln
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    lsSet(Keys.THEME, theme);
  }, [theme]);

  // currentChatId in localStorage spiegeln
  useEffect(() => {
    if (currentChatId == null) lsRemove(Keys.CHAT_ID);
    else lsSet(Keys.CHAT_ID, String(currentChatId));
  }, [currentChatId]);

  // API-Client mit Auto-Logout bei 401
  const api = useMemo(() => makeApi({
    getToken: () => authToken,
    onUnauthorized: logout,
  }), [authToken, logout]);

  const { chats, refresh, createChat, renameChat, deleteChat, togglePin } = useChats(api);

  // Pending Initial-Prompt (z.B. von Report→Chat) — wird in Chat als Eingabe eingespielt
  const [pendingPrompt, setPendingPrompt] = useState(null);
  // Pending Job-Topic (von Chat → /schedule) — JobsView öffnet Form vorbefüllt
  const [pendingJobTopic, setPendingJobTopic] = useState(null);

  // Falls aktiver Chat gelöscht wurde: zurücksetzen
  useEffect(() => {
    if (currentChatId != null && chats.length > 0 && !chats.some(c => c.id === currentChatId)) {
      setCurrentChatId(null);
    }
  }, [chats, currentChatId]);

  const handleNewChat = useCallback(() => setCurrentChatId(null), []);

  const handleChatChanged = useCallback((newId) => {
    setCurrentChatId(prev => (newId !== prev ? newId : prev));
    refresh();
  }, [refresh]);

  const handleDelete = useCallback(async (id) => {
    await deleteChat(id);
    setCurrentChatId(prev => (prev === id ? null : prev));
  }, [deleteChat]);

  /** Chat → Job: Tab wechseln + Job-Form mit Topic vorbefüllen. */
  const handleScheduleFromChat = useCallback((topic) => {
    setPendingJobTopic(topic);
    setTab('jobs');
  }, []);

  /** Report → Chat: neuen Chat öffnen, Report als initialen Kontext einspielen. */
  const handleDiscussReport = useCallback(async (report) => {
    const id = await createChat();
    if (id) setCurrentChatId(id);
    const head = report.topic ? `**Recherche-Report: ${report.topic}**\n\n` : '';
    setPendingPrompt(`Bitte fasse diesen Report kurz zusammen und nenne 3 spannendste Punkte:\n\n${head}${report.content || ''}`);
    setTab('chat');
  }, [createChat]);

  /** Idee → Chat: neuen Chat öffnen, Idee als Briefing für Artikel-Entwurf einspielen. */
  const handleDiscussIdea = useCallback(async (idea) => {
    const id = await createChat();
    if (id) setCurrentChatId(id);
    const parts = [`**Artikel-Idee: ${idea.title}**`];
    if (idea.hook)          parts.push(`Hook: ${idea.hook}`);
    if (idea.kuka2me_angle) parts.push(`KUKA2ME-Winkel: ${idea.kuka2me_angle}`);
    if (idea.format_suggestion) parts.push(`Format: ${idea.format_suggestion}`);
    if (Array.isArray(idea.source_urls) && idea.source_urls.length)
      parts.push(`Quellen:\n${idea.source_urls.map(u => `- ${u}`).join('\n')}`);
    // Arbeitsstand priorisieren: Entwurf > Gliederung > nur Idee
    let ask;
    if (idea.draft) {
      ask = `Hilf mir, diesen KUKA2ME-Artikel-Rohentwurf redaktionell zu überarbeiten: schärfe Argumentation, prüfe die mit [RECHERCHE NÖTIG] markierten Stellen und verbessere den Stil. Nichts erfinden:\n\n${idea.draft}`;
    } else if (idea.outline) {
      ask = `Hilf mir, aus dieser Artikel-Idee einen Text-Entwurf zu entwickeln. Die Redaktions-Gliederung existiert bereits — arbeite damit:\n\n${parts.join('\n')}\n\n--- Vorhandene Gliederung ---\n${idea.outline}`;
    } else {
      ask = `Hilf mir, aus dieser Artikel-Idee einen Entwurf zu entwickeln. Erstelle zuerst eine Gliederung:\n\n${parts.join('\n')}`;
    }
    setPendingPrompt(ask);
    setTab('chat');
  }, [createChat]);

  const chatListProps = useMemo(() => ({
    chats,
    currentId: currentChatId,
    onSelect:  setCurrentChatId,
    onNew:     handleNewChat,
    onRename:  renameChat,
    onDelete:  handleDelete,
    onPin:     togglePin,
  }), [chats, currentChatId, handleNewChat, renameChat, handleDelete, togglePin]);

  if (!authToken) return <AuthScreen onAuth={setAuthToken} />;

  // Mobile-Helper: Tab-Wechsel schließt Drawer; Chat-Wechsel auch.
  const setTabAndClose = (t) => { setTab(t); setSidebarOpen(false); };
  const wrappedChatListProps = {
    ...chatListProps,
    onSelect: (id) => { chatListProps.onSelect(id); setSidebarOpen(false); },
    onNew:    () => { chatListProps.onNew(); setSidebarOpen(false); },
  };

  const openHelp = (sectionId) => setHelpSection(sectionId || 'ueberblick');

  return (
    <div className={`app-shell ${sidebarOpen ? 'sidebar-open' : ''}`}>
      {helpSection && (
        <HelpModal initialSection={helpSection} onClose={() => setHelpSection(null)} />
      )}
      <Sidebar tab={tab} setTab={setTabAndClose} onLogout={logout}
               chatListProps={wrappedChatListProps}
               theme={theme} setTheme={setTheme}
               authToken={authToken} api={api}
               onHelp={openHelp}
               onSearchSelectChat={(id) => { setCurrentChatId(id); setTab('chat'); setSidebarOpen(false); }} />
      <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      <main className="main-panel">
        <button className="mobile-hamburger" onClick={() => setSidebarOpen(true)} title="Menü">☰</button>
        <ErrorBoundary key={tab}>
          {tab === 'chat' && (
            <Chat
              authToken={authToken}
              onLogout={logout}
              currentChatId={currentChatId}
              onChatChanged={handleChatChanged}
              initialPrompt={pendingPrompt}
              onInitialPromptConsumed={() => setPendingPrompt(null)}
              onScheduleFromChat={handleScheduleFromChat}
              onHelp={openHelp}
            />
          )}
          {tab === 'jobs' && (
            <JobsView
              authToken={authToken}
              onLogout={logout}
              onDiscussReport={handleDiscussReport}
              initialTopic={pendingJobTopic}
              onInitialTopicConsumed={() => setPendingJobTopic(null)}
              onHelp={openHelp}
            />
          )}
          {tab === 'ideas' && (
            <IdeasView
              authToken={authToken}
              onLogout={logout}
              onDiscussIdea={handleDiscussIdea}
              onHelp={openHelp}
              openIdeaId={pendingOpenIdea}
              onOpenIdeaConsumed={() => setPendingOpenIdea(null)}
            />
          )}
          {tab === 'wissen' && (
            <KnowledgeView authToken={authToken} onLogout={logout} onHelp={openHelp} />
          )}
          {tab === 'posting' && (
            <PostingView authToken={authToken} onLogout={logout} onHelp={openHelp}
              onOpenIdea={(id) => { setTab('ideas'); setPendingOpenIdea(id); }} />
          )}
        </ErrorBoundary>
      </main>
    </div>
  );
}
